/**
 * Disposal tax entry points for investment property and partial share/managed-fund sales, plus the
 * accountant CGT override.
 *
 * The CGT MATH ITSELF now lives in ./disposalCgt.ts — this module is a thin adapter that maps the
 * long-standing call signatures (and the UI's free-text cost base field) onto it. It previously held
 * its own copy of the cost-base/gain/marginal-rate logic, which had drifted from the three other
 * copies elsewhere in the codebase; see disposalCgt.ts's header for what that drift caused.
 *
 * Note: this file and ./support/entityFundingUtils.ts import from each other (see that file's
 * header comment) — a pre-existing, safe circular import (function-body-only references).
 */
import { isEntityOwnership } from './constants/clientStructureTypes.ts';
import { computeDisposalCgt, type DisposalCgtResult } from './disposalCgt.ts';
import { resolveDisposalTaxContext, type FinancialsWithTaxContext } from './disposalTaxContext.ts';
import { findAssetInFactFind, parseRadFundingAssetKey } from './support/entityFundingUtils.ts';
import type { FactFind, FactFindAsset } from './factFindTypes.ts';

export { TRUST_RETAIN_TAX_RATE } from './disposalCgt.ts';

/** Parse optional accountant CGT override (non-negative number, or null to use calculated). */
export function parseCgtOverride(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === '') return null;
    const n = parseFloat(String(raw));
    return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Apply optional override to a disposal tax summary (display + net proceeds). */
export function getEffectiveDisposalCgt(taxSummary: any, overrideTotal: unknown) {
    if (!taxSummary) {
        return { calculatedCGT: 0, estimatedCGT: 0, effectiveCGT: 0, isOverridden: false, netProceeds: 0 };
    }
    const calculatedCGT = taxSummary.estimatedCGT ?? 0;
    const override = parseCgtOverride(overrideTotal);
    const isOverridden = override != null;
    const effectiveCGT = isOverridden ? override : calculatedCGT;
    const salePrice = taxSummary.salePrice ?? 0;
    const sellingCosts = taxSummary.sellingCosts ?? 0;
    return {
        ...taxSummary,
        calculatedCGT,
        estimatedCGT: effectiveCGT,
        effectiveCGT,
        isOverridden,
        netProceeds: salePrice - sellingCosts - effectiveCGT,
    };
}

/** Sum effective CGT across RAD funding share disposals (for liquidity warnings). */
export function sumRadFundingShareCgt(factFind: FactFind, radFunding: Record<string, unknown> = {}, cgtOverrides: Record<string, unknown> = {}): number {
    if (!radFunding) return 0;
    let total = 0;
    Object.entries(radFunding).forEach(([assetKey, saleAmountRaw]) => {
        const parsed = parseRadFundingAssetKey(assetKey);
        if (!parsed || parsed.assetType !== 'shares') return;
        const saleAmount = parseFloat(String(saleAmountRaw)) || 0;
        if (saleAmount <= 0) return;
        const located = findAssetInFactFind({ assets: factFind?.assets }, parsed.assetType, parsed.assetId);
        if (!located?.asset?.costBase) return;
        const taxResult = calculateSharePartialDisposalTax(factFind, located.asset, saleAmount, { trustDisposal: 'distribute' });
        const effective = getEffectiveDisposalCgt(taxResult, cgtOverrides[assetKey]);
        total += effective.effectiveCGT || 0;
    });
    return total;
}

/** Pre-CGT exemption is decided inside the CGT engine, from the asset's recorded acquisition month.
 *  A BLANK cost base is "unknown" (=> 25% assumed default downstream, see resolveCostBase), not exempt. */
export function parseCostBaseForCgt(costBaseText: unknown): { costBase: number } {
    return { costBase: parseFloat(String(costBaseText ?? '').trim()) || 0 };
}

/** Options shared by the disposal entry points below. */
interface DisposalOptions {
    trustDisposal?: 'retain' | 'distribute';
    /** Disposal date — drives which CGT regime applies. Defaults to today inside the engine. */
    disposalDate?: Date | string;
    /** CPI for indexing the post-1-Jul-2027 slice of a gain. Defaults to the CGT_REGIME assumption. */
    cpiRate?: number;
    /** Pre-computed household financials, to avoid recomputing them. */
    financials?: FinancialsWithTaxContext | null;
}

function runDisposal(
    factFind: FactFind,
    asset: FactFindAsset | null | undefined,
    params: {
        salePrice: number;
        sellingCosts: number;
        costBaseText?: unknown;
        originalAssetValue?: number;
    } & DisposalOptions,
): DisposalCgtResult {
    const { costBase: parsedCostBase } = parseCostBaseForCgt(params.costBaseText ?? '');
    return computeDisposalCgt({
        taxContext: resolveDisposalTaxContext(factFind, params.financials),
        asset,
        salePrice: params.salePrice,
        sellingCosts: params.sellingCosts,
        originalAssetValue: params.originalAssetValue,
        costBaseOverride: parsedCostBase > 0 ? parsedCostBase : null,
        disposalDate: params.disposalDate,
        cpiRate: params.cpiRate,
        structures: factFind?.structures,
        trustDisposal: params.trustDisposal,
    });
}

export function calculateInvestmentPropertyDisposalTax(factFind: FactFind, asset: FactFindAsset | null | undefined, details: any = {}) {
    const salePrice = details.salePrice || asset?.value || 0;
    const sellingCosts = details.sellingCosts || salePrice * 0.025;
    return runDisposal(factFind, asset, {
        salePrice,
        sellingCosts,
        costBaseText: details.costBase ?? asset?.costBase ?? '',
        trustDisposal: details.trustDisposal,
        disposalDate: details.disposalDate,
        cpiRate: details.cpiRate,
        financials: details.financials,
    });
}

/** Partial liquidation of shares / managed funds for RAD funding (proportional cost base). */
export function calculateSharePartialDisposalTax(factFind: FactFind, asset: FactFindAsset | null | undefined, saleAmount: number, details: any = {}) {
    const salePrice = saleAmount || 0;
    const currentValue = asset?.value || 0;
    // `originalValue` is the holding's value BEFORE this sale — it is what the cost base gets apportioned
    // against. It defaults to the asset's CURRENT value because every caller passes an asset that has not
    // yet been reduced by the sale (the reduction happens afterwards, e.g. assetLiquidation.ts mutates a
    // clone). `details.originalValue` remains available for any caller that DOES pre-reduce the asset.
    //
    // This previously defaulted to `currentValue + salePrice`, which assumed the asset had already been
    // decremented. No caller does, so the pre-sale value was overstated — for a full sale it doubled, which
    // apportioned only half the cost base to the sale and inflated the gain (a $200k holding with a $150k
    // cost base showed a $125k gain instead of $50k). Note it only ever affected assets with an ENTERED
    // cost base: where none is recorded the 25%-of-value assumption is itself proportional to the value, so
    // the two errors cancelled exactly.
    const originalValue = details.originalValue != null ? details.originalValue || 0 : currentValue;
    return runDisposal(factFind, asset, {
        salePrice,
        // Brokerage on a share sale is immaterial at this level of estimate.
        sellingCosts: 0,
        costBaseText: asset?.costBase ?? '',
        originalAssetValue: originalValue,
        trustDisposal: details.trustDisposal,
        disposalDate: details.disposalDate,
        cpiRate: details.cpiRate,
        financials: details.financials,
    });
}

/**
 * RAD share CGT uses Option A (net proceeds pool): tax is deducted before RAD is credited,
 * not as a separate cashflow line. Returns no extra cashflow items.
 */
export function applyRadFundingCgt() {
    return { extraCashflowItems: [] as any[] };
}

/** @deprecated Use calculateInvestmentPropertyDisposalTax — kept for importers expecting same shape */
export function getInvestmentPropertyTaxSummary(factFind: FactFind, investmentPropertyDetails: any) {
    if (!investmentPropertyDetails || !factFind?.assets?.investmentProperty?.length) {
        return null;
    }
    const selectedPropertyId = investmentPropertyDetails.selectedPropertyId;
    const investmentProperty =
        factFind.assets.investmentProperty.find((p) => p.id === selectedPropertyId) || factFind.assets.investmentProperty[0];
    if (!investmentProperty) return null;

    const result = calculateInvestmentPropertyDisposalTax(factFind, investmentProperty, investmentPropertyDetails);
    const effective: any = getEffectiveDisposalCgt(result, investmentPropertyDetails?.cgtOverrideTotal);
    if (isEntityOwnership(investmentProperty.ownership) && effective.entityDisposalWarning) {
        effective.warning = effective.entityDisposalWarning;
    } else if (isEntityOwnership(investmentProperty.ownership) && !effective.entityDisposalWarning && effective.taxMethod !== 'personal_marginal') {
        effective.warning = null;
    }
    return effective;
}
